import { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import InfiniteScroll from 'react-infinite-scroll-component';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import useAuth from '../../../hooks/useAuth';
import { jwtDecode } from "jwt-decode";
import BarberAppointmentsTable from "./BarberAppointmentsTable";

export default function BarberAppointments() {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();
  const decoded = auth?.token ? jwtDecode(auth.token) : undefined;

  const apiUrl =
    process.env.NODE_ENV === 'development'
      ? process.env.REACT_APP_API_LOCAL
      : process.env.REACT_APP_API;

  const APPOINTMENTS_PER_PAGE = 20;

  useEffect(() => {
    fetchAppointments(currentPage);
  }, []);

  const fetchAppointments = async (page) => {
    if (!decoded?.id) return;
    try {
      const today = new Date();
      const thirtyDaysLater = new Date();
      thirtyDaysLater.setDate(today.getDate() + 30);

      const response = await axiosPrivate.get(
        `${apiUrl}/appointment-details/${decoded.id}`,
        {
          params: {
            from: today.toISOString().split('T')[0],
            to: thirtyDaysLater.toISOString().split('T')[0],
            page: page,
            limit: APPOINTMENTS_PER_PAGE,
          },
        }
      );

      const newAppointments = response.data.appointments;

      setAppointments(prev => [...prev, ...newAppointments]);
      setCurrentPage(page);
      setHasMore(newAppointments.length === APPOINTMENTS_PER_PAGE);
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setError('Problem sa serverom, probaj Logout i Login. Pa zovi Darka :).');
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // helper functions
  const convertTime = (timeString) => {
    const timeParts = timeString.split(':');
    return timeParts.slice(0, 2).join(':');
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    const date = new Date(dateString);
    return date.toLocaleString('en-US', options);
  };

  const calculateEndTime = (startTime, durationInMinutes) => {
    const timeParts = startTime.split(':');
    const hours = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);
    const seconds = parseInt(timeParts[2], 10);

    const startDate = new Date();
    startDate.setHours(hours, minutes, seconds, 0);
    startDate.setMinutes(startDate.getMinutes() + durationInMinutes);

    const endHours = startDate.getHours().toString().padStart(2, '0');
    const endMinutes = startDate.getMinutes().toString().padStart(2, '0');

    return `${endHours}:${endMinutes}`;
  };

  const deleteAppointment = async (appointmentId) => {
    if (!appointmentId) {
      console.error('Invalid appointmentId:', appointmentId);
      setError('Invalid appointment ID');
      return;
    }

    try {
      const response = await axiosPrivate.delete(`${apiUrl}/appointment/${appointmentId}`);
      if (response.status !== 200) throw new Error('Failed to delete appointment');

      setAppointments((prev) =>
        prev.filter((appointment) => appointment.appointmentId !== appointmentId)
      );
    } catch (error) {
      console.error('Error deleting appointment:', error);
      setError('Problem sa serverom, probaj Logout i Login. Pa zovi Darka :).');
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center bg-zinc-950 p-6 w-full">
      {error && <p className="text-red-500 mb-4">{error}</p>}

      <InfiniteScroll
        dataLength={appointments.length}
        next={() => fetchAppointments(currentPage + 1)}
        hasMore={hasMore}
        loader={<p className="text-white text-center mt-4">Loading more appointments...</p>}
        endMessage={<p className="text-white text-center mt-4">All appointments loaded.</p>}
        scrollableTarget="scrollableDiv"
      >
        <BarberAppointmentsTable
          appointments={appointments}
          today={today}
          deleteAppointment={deleteAppointment}
          calculateEndTime={calculateEndTime}
          convertTime={convertTime}
          formatDate={formatDate}
        />
      </InfiniteScroll>

      <button
        onClick={() => navigate("/barber-dashboard")}
        className="mt-6 w-full max-w-xs p-4 bg-zinc-800 text-white rounded-2xl shadow hover:bg-zinc-700 transition"
      >
        ← Nazad na Dashboard
      </button>
    </div>
  );
}
